export interface TemplateVariables {
  itemName?: string;
  uom?: string;
  quantity?: string;
  date?: string;
  [key: string]: string | undefined; // Allow for additional dynamic variables
}

export interface Template {
  id?: string;
  estimateid?: string;
  templateName: string;
  templateType: TemplateType;
  templateText: string;
  createdAt?: Date;
  updatedAt?: Date;
  description?: string;
  requiredVariables?: (keyof TemplateVariables)[]; // List of required variables for this template
}

export type TemplateType =
  | 'proposal'
  | 'request-for-quote'
  | 'scope-of-work'
  | 'labor-unit-notes'
  | 'material-takeoff-notes'
  | 'subcontractor-comparison'
  | 'cost-analysis'
  | 'site-visit-notes'
  | 'client-meeting-notes'
  | 'design-review-notes'
  | 'budget-breakdown'
  | 'risk-assessment'
  | 'general';

export const TEMPLATE_TYPE_OPTIONS: { value: TemplateType; label: string }[] = [
  { value: 'proposal', label: 'Proposal' },
  { value: 'request-for-quote', label: 'Request for Quote (RFQ)' },
  { value: 'scope-of-work', label: 'Scope of Work' },
  { value: 'labor-unit-notes', label: 'Labor Unit Notes' },
  { value: 'material-takeoff-notes', label: 'Material Takeoff Notes' },
  { value: 'subcontractor-comparison', label: 'Subcontractor Comparison' },
  { value: 'cost-analysis', label: 'Cost Analysis' },
  { value: 'site-visit-notes', label: 'Site Visit Notes' },
  { value: 'client-meeting-notes', label: 'Client Meeting Notes' },
  { value: 'design-review-notes', label: 'Design Review Notes' },
  { value: 'budget-breakdown', label: 'Budget Breakdown' },
  { value: 'risk-assessment', label: 'Risk Assessment' },
  { value: 'general', label: 'General' },
];

// Hardcoded starter templates
export const STARTER_TEMPLATES: Template[] = [
  {
    id: 'starter-labor-unit-notes-v2',
    templateName: '🔧 Labor Unit Notes Template',
    templateType: 'labor-unit-notes',
    description: 'Simple breakdown of the five labor slices',
    requiredVariables: ['itemName', 'uom', 'uomValue'], // Specify which variables this template needs
    templateText: `<div style="font-family: Arial, sans-serif; line-height: 1.4;">
  <h2>Installation Description for {{itemName}}</h2>
  <p>Unit of Measure: {{uom}}</p>
  <p>Unit of Measure Value: {{uomValue}}</p>

  <div style="margin: 16px 0;">
    <h3>1. Installation</h3>
    <ul>
      <li>Core work: cutting, fitting, fastening, testing</li>
      <li>What specific steps are needed?</li>
    </ul>
  </div>

  <div style="margin: 16px 0;">
    <h3>2. Layout</h3>
    <ul>
      <li>Measuring, marking, planning routes</li>
      <li>Any special coordination needed?</li>
    </ul>
  </div>

  <div style="margin: 16px 0;">
    <h3>3. Material Cleanup</h3>
    <ul>
      <li>Moving materials to work area</li>
      <li>Daily cleanup requirements</li>
    </ul>
  </div>

  <div style="margin: 16px 0;">
    <h3>4. Non-Productive Time</h3>
    <ul>
      <li>Brief delays, breaks, discussions</li>
      <li>Access requirements (ladders, lifts)</li>
    </ul>
  </div>

  <div style="margin: 16px 0;">
    <h3>5. Tool Handling</h3>
    <ul>
      <li>Tool setup and teardown</li>
      <li>Equipment needs and maintenance</li>
    </ul>
  </div>

  <div style="margin: 24px 0;">
    <h3>Labor Units that Should Not Be Included:</h3>
    <ul>
      <li>Height/access considerations.</li>
      <li>Space constraints.</li>
      <li>Weather impacts.</li>
      <li>Access to the work area.</li>
      <li>Supervision. We account for this labor separately.</li>
      <li>Any other labor that is extraneous to the item being analyzed.</li>
    </ul>
  </div>
</div>`,
  },
  {
    id: 'starter-proposal-comprehensive',
    templateName: '📑 Comprehensive Construction Proposal',
    templateType: 'proposal',
    description:
      'Full-length proposal with executive summary, scope, pricing, schedule, payment terms, and standard clauses',
    templateText: `
  <div style="font-family: Arial, sans-serif; line-height: 1.4; max-width: 750px; margin: 0 auto;">
  
    <!-- === HEADER & PROJECT INFO ============================================== -->
    <p style="margin-bottom: 20px;">
      <strong>[YOUR COMPANY NAME]</strong><br>
      [STREET ADDRESS]<br>
      [CITY], [STATE] [ZIP]<br>
      Phone: [PHONE] &nbsp;|&nbsp; Email: [EMAIL]
    </p>
  
    <p>
      <strong>Proposal No.:</strong> [PROPOSAL #]<br>
      <strong>Date:</strong> [MM/DD/YYYY]<br>
      <strong>Prepared For:</strong> [CLIENT / OWNER NAME]<br>
      <strong>Project:</strong> [PROJECT TITLE / LOCATION]
    </p>
  
    <hr style="margin: 24px 0; border: 1px solid #bbb;">
  
    <!-- === EXECUTIVE SUMMARY =================================================== -->
    <h3>🏗️ Executive Summary</h3>
    <p>
      [BRIEFLY DESCRIBE THE PROJECT, CLIENT'S GOALS, AND HOW YOUR COMPANY WILL
      DELIVER VALUE—USUALLY 3–5 SENTENCES.]
    </p>
  
    <!-- === SCOPE OF WORK ====================================================== -->
    <h3>📋 Scope of Work</h3>
    <p>
      The following work will be performed by <strong>[YOUR COMPANY NAME]</strong>
      (“Contractor”):
    </p>
    <ul>
      <li>[TASK #1]</li>
      <li>[TASK #2]</li>
      <li>[TASK #3]</li>
    </ul>
  
    <!-- === INCLUSIONS & EXCLUSIONS ============================================ -->
    <h4>Inclusions</h4>
    <ul><li>[LIST EVERYTHING INCLUDED—MATERIALS, PERMITS, TESTING, ETC.]</li></ul>
  
    <h4>Exclusions</h4>
    <ul><li>[LIST ITEMS NOT INCLUDED—UTILITY FEES, HAZMAT ABATEMENT, ETC.]</li></ul>
  
    <!-- === ASSUMPTIONS / QUALIFICATIONS ======================================= -->
    <h3>📝 Assumptions & Qualifications</h3>
    <ul>
      <li>[E.G., “Work to be performed during normal business hours (7 AM – 5 PM).”]</li>
      <li>[E.G., “Site must be clear and accessible on agreed start date.”]</li>
      <li>[E.G., “All underground utilities have been clearly marked by owner.”]</li>
    </ul>
  
    <!-- === PRICING ============================================================ -->
    <h2>💰 Pricing</h2>
    <p>
      <strong>Total Lump-Sum Price:</strong> $[TOTAL AMOUNT]
    </p>

    <h3>Price Breakouts</h3>
    <p><em>Key components of the total price are broken out below for reference:</em></p>

    <ul style="list-style: none; padding-left: 0;">
      <li style="margin-bottom: 1em;">
        <strong>1. Equipment & Materials</strong><br>
        • [Major Equipment Item 1] ....................................... $[AMOUNT]<br>
        • [Major Equipment Item 2] ....................................... $[AMOUNT]<br>
        <div style="margin: 4px 0 4px 20px; color: #666;">
          <em>Includes: [Brief description of what's included]</em>
        </div>
      </li>

      <li style="margin-bottom: 1em;">
        <strong>2. Labor</strong><br>
        • [Labor Category 1] ............................................. $[AMOUNT]<br>
        • [Labor Category 2] ............................................. $[AMOUNT]<br>
        <div style="margin: 4px 0 4px 20px; color: #666;">
          <em>Based on: [Brief explanation of labor calculation]</em>
        </div>
      </li>

      <li style="margin-bottom: 1em;">
        <strong>3. Subcontractor Costs</strong><br>
        • [Subcontractor Scope 1] ....................................... $[AMOUNT]<br>
        • [Subcontractor Scope 2] ....................................... $[AMOUNT]<br>
        <div style="margin: 4px 0 4px 20px; color: #666;">
          <em>Includes: [Brief description of subcontractor scope]</em>
        </div>
      </li>

      <li style="margin-bottom: 1em;">
        <strong>4. Other Direct Costs</strong><br>
        • [Cost Category 1] ............................................. $[AMOUNT]<br>
        • [Cost Category 2] ............................................. $[AMOUNT]<br>
        <div style="margin: 4px 0 4px 20px; color: #666;">
          <em>Includes: [Brief description of other costs]</em>
        </div>
      </li>
    </ul>

    <blockquote style="margin-top: 1.5em;">
      <p><strong>Note:</strong> The breakouts above represent key components of the total price. Additional details available upon request.</p>
    </blockquote>

    <!-- === PAYMENT TERMS ====================================================== -->
    <h3>💳 Payment Terms</h3>
    <ul>
      <li>[E.G., “10 % deposit due at signing; balance invoiced monthly, 30-day net.”]</li>
      <li>[E.G., “Retainage of 10 % released upon substantial completion.”]</li>
    </ul>
  
    <!-- === PROJECT SCHEDULE =================================================== -->
    <h3>📆 Project Schedule</h3>
    <p>
      <strong>Estimated Start:</strong> [DATE] &nbsp; | &nbsp;
      <strong>Substantial Completion:</strong> [DATE]
    </p>
    <ul>
      <li>Mobilization – [DATE]</li>
      <li>Major Milestone – [DATE]</li>
      <li>Final Inspection – [DATE]</li>
    </ul>
  
    <!-- === CHANGE ORDERS ====================================================== -->
    <h3>🔄 Change Order Procedure</h3>
    <p>
      Any work outside the defined scope must be authorized via written change
      order. Pricing and schedule impacts will be provided for client approval
      before additional work begins.
    </p>
  
    <!-- === WARRANTY =========================================================== -->
    <h3>🔧 Warranty</h3>
    <p>
      Contractor warrants workmanship for [12] months from date of substantial
      completion. Manufacturer warranties apply to all equipment and materials.
    </p>
  
    <!-- === CLAUSES ============================================================ -->
    <h3>⚖️ Standard Clauses</h3>
    <ul>
      <li><strong>Price Escalation:</strong> If material costs rise more than
          [X] % between proposal date and delivery, Contractor may request an
          equitable adjustment with supporting documentation.</li>
      <li><strong>Force Majeure:</strong> Delays caused by events beyond
          Contractor's control (weather, strikes, supply shortages, etc.) shall
          extend the schedule without penalty.</li>
      <li><strong>Safety & Insurance:</strong> Contractor maintains all required
          insurance and follows OSHA safety standards.</li>
      <li><strong>Dispute Resolution:</strong> Any dispute not resolved informally
          will be settled by [MEDIATION / ARBITRATION / COURT] in the state of
          [STATE].</li>
    </ul>
  
    <!-- === ACCEPTANCE ========================================================= -->
    <hr style="margin: 32px 0; border: 1px solid #bbb;">
    <p>
      By signing below, the Client acknowledges acceptance of this proposal and
      the attached terms and conditions.
    </p>
  
    <p style="margin-top: 40px;">
      _________________________________________ &nbsp;&nbsp; ____________<br>
      Authorized Signature &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date
    </p>
  
    <p>
      Printed Name: __________________________________<br>
      Title: _________________________________________
    </p>
  
    <p style="font-size: 0.85em; color: #555; margin-top: 40px;">
      <em>Replace all bracketed [ ] items with project-specific details.</em>
    </p>
  </div>
  `,
  },
  {
    id: 'starter-material-takeoff-notes',
    templateName: '📐 Material Takeoff Notes Starter',
    templateType: 'material-takeoff-notes',
    description:
      'Comprehensive template for documenting material takeoff calculations and installation notes',
    templateText: `<h1>Material Takeoff: [ITEM/ASSEMBLY NAME]</h1>

<h2>📋 Item Details</h2>
<p><strong>Item Description:</strong><br>
[DETAILED DESCRIPTION OF THE MATERIAL/COMPONENT]</p>

<p><strong>Unit of Measure:</strong> [UOM - e.g., LF, SF, EA, CY]<br>
<strong>Quantity Required:</strong> [TOTAL QUANTITY]<br>
<strong>Location/Area:</strong> [BUILDING/ROOM/ZONE]</p>

<h2>💰 Cost Information</h2>
<p><strong>Unit Cost:</strong> $[COST] per [UOM]<br>
<strong>Total Material Cost:</strong> $[TOTAL COST]<br>
<strong>Labor Hours per Unit:</strong> [HOURS] hours</p>

<h2>🔧 Installation Requirements</h2>
<ul>
  <li>
    <strong>Preparation Work:</strong><br>
    [SURFACE PREP, LAYOUT, MEASUREMENTS]
  </li>
  <li>
    <strong>Installation Method:</strong><br>
    [STEP-BY-STEP INSTALLATION PROCESS]
  </li>
  <li>
    <strong>Quality Control:</strong><br>
    [INSPECTION POINTS, TESTING REQUIREMENTS]
  </li>
</ul>

<h2>📏 Measurement & Calculation Notes</h2>
<ul>
  <li>
    <strong>Measurement Method:</strong><br>
    [HOW QUANTITIES WERE DETERMINED]
  </li>
  <li>
    <strong>Waste Factor:</strong><br>
    [PERCENTAGE] % waste allowance included
  </li>
  <li>
    <strong>Field Verification:</strong><br>
    [NOTES FROM SITE VISIT/MEASUREMENTS]
  </li>
</ul>

<h2>⚠️ Special Considerations</h2>
<ul>
  <li>
    <strong>Access/Logistics:</strong><br>
    [DELIVERY, STAGING, EQUIPMENT NEEDS]
  </li>
  <li>
    <strong>Coordination:</strong><br>
    [OTHER TRADES, SEQUENCING REQUIREMENTS]
  </li>
  <li>
    <strong>Code/Standards:</strong><br>
    [APPLICABLE CODES, SPECIFICATIONS]
  </li>
  <li>
    <strong>Safety Notes:</strong><br>
    [SAFETY REQUIREMENTS, PPE, HAZARDS]
  </li>
</ul>

<h2>📝 Additional Notes</h2>
<p>[ANY OTHER RELEVANT INFORMATION, ASSUMPTIONS, OR CLARIFICATIONS]</p>

<blockquote>
  <p>📌 Replace all [BRACKETED] items with your specific project details</p>
</blockquote>`,
  },
];

export interface SaveTemplateRequest {
  templateName: string;
  templateType: TemplateType;
  templateText: string;
}
